import React from 'react';
import * as Icons from 'lucide-react';

const OrderChangesModal = ({ isOpen, onClose, order, onValidateNotes }) => {
    const [isValidating, setIsValidating] = React.useState(false);

    if (!isOpen || !order) return null;

    const snapshot = typeof order.packing_snapshot === 'string'
        ? JSON.parse(order.packing_snapshot)
        : order.packing_snapshot;

    const snapItems = snapshot?.items || [];
    const currentItems = order.items || [];

    // Lógica de comparación
    const getChanges = () => {
        const added = [];
        const removed = [];
        const modified = [];
        const unchanged = [];

        const snapMap = new Map();
        snapItems.forEach(item => snapMap.set(item.product_code || item.id, item));

        const currentMap = new Map();
        currentItems.forEach(item => currentMap.set(item.product_code || item.id, item));

        // Buscar modificados y agregados
        currentItems.forEach(item => {
            const key = item.product_code || item.id;
            const snapItem = snapMap.get(key);

            if (!snapItem) {
                added.push(item);
            } else {
                const snapPrice = parseFloat(snapItem.price || snapItem.unit_price || 0);
                const currentPrice = parseFloat(item.price || item.unit_price || 0);
                // Account for IVA: snapshot may store IVA-inclusive prices while Siigo sync
                // overwrites with base prices. Check both direct match and IVA-adjusted match.
                const taxPercent = parseFloat(snapItem.tax_percent || item.tax_percent || 0);
                const snapPriceBase = taxPercent > 0 ? snapPrice / (1 + taxPercent / 100) : snapPrice;
                const priceChanged = Math.abs(snapPrice - currentPrice) > 1 && Math.abs(snapPriceBase - currentPrice) > 1;
                const qtyChanged = parseInt(snapItem.quantity) !== parseInt(item.quantity);

                if (priceChanged || qtyChanged) {
                    modified.push({
                        current: item,
                        snap: snapItem,
                        priceChanged,
                        qtyChanged
                    });
                } else {
                    unchanged.push(item);
                }
            }
        });

        // Buscar eliminados
        snapItems.forEach(item => {
            const key = item.product_code || item.id;
            if (!currentMap.has(key)) {
                removed.push(item);
            }
        });

        return { added, removed, modified, unchanged };
    };

    const { added, removed, modified } = getChanges();

    // Comparación de notas
    const stripSystemTags = (text) => {
        if (!text) return '';
        return text
            .replace(/\[AUTO-RC:[\s\S]*?\](?:\s*\([\s\S]*?\))?/gi, '')
            .replace(/\[WATCHDOG[\s\S]*?\]/gi, '')
            .replace(/Pedido creado desde factura SIIGO:[\s\S]*?\n?/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const currentNotes = order.notes || order.siigo_observations || '';
    const snapNotes = snapshot?.notes || snapshot?.siigo_observations || '';

    // Mostramos las notas ORIGINALES en la UI, pero usamos las llimpias para comparar si cambiaron
    const currentNotesClean = stripSystemTags(currentNotes);
    const snapNotesClean = stripSystemTags(snapNotes);

    const notesChanged = currentNotesClean !== snapNotesClean;



    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-100 text-rose-600 rounded-lg">
                            <Icons.ClipboardList className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-gray-900 leading-none">Comparación de Cambios</h2>
                            <p className="text-sm text-gray-500 mt-1 font-bold">Pedido {order.order_number}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                    >
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto space-y-8">
                    {/* Alerta inicial */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
                        <Icons.AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-amber-900 font-bold">Se detectaron modificaciones después del empaque.</p>
                            <p className="text-xs text-amber-700 mt-0.5">
                                Snapshot capturado el: <span className="font-bold">{snapshot?.captured_at ? new Date(snapshot.captured_at).toLocaleString() : 'N/A'}</span>
                            </p>
                        </div>
                    </div>

                    {/* Sección de Productos */}
                    <div className="space-y-4">
                        <h3 className="text-base font-black text-gray-800 flex items-center gap-2 uppercase tracking-wider">
                            <Icons.Package className="w-4 h-4" />
                            Productos y Cantidades
                        </h3>

                        <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[10px] tracking-widest border-b border-gray-100">
                                    <tr>
                                        <th className="px-4 py-3">Producto</th>
                                        <th className="px-4 py-3 text-center">Antes (Snp)</th>
                                        <th className="px-4 py-3 text-center">Después (Act)</th>
                                        <th className="px-4 py-3 text-right">Precio Unit. (Cambio)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {/* Modificados */}
                                    {modified.map(({ current, snap, priceChanged, qtyChanged }) => (
                                        <tr key={current.id} className="bg-blue-50/30">
                                            <td className="px-4 py-3">
                                                <div className="font-black text-gray-900">{current.product_code || '-'}</div>
                                                <div className="text-[11px] text-gray-500 font-bold truncate max-w-[200px]">{current.name || current.product_name}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center font-bold text-gray-400">{snap.quantity}</td>
                                            <td className={`px-4 py-3 text-center font-black ${qtyChanged ? 'text-blue-600 underline decoration-2' : 'text-gray-900'}`}>
                                                {current.quantity}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="text-gray-400 text-xs line-through">{formatCurrency(snap.price || snap.unit_price)}</div>
                                                <div className={`font-black ${priceChanged ? 'text-orange-600' : 'text-gray-900'}`}>
                                                    {formatCurrency(current.price || current.unit_price)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Agregados */}
                                    {added.map(item => (
                                        <tr key={item.id} className="bg-emerald-50/40 font-bold">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-emerald-500 text-white text-[9px] px-1.5 py-0.5 rounded uppercase leading-none">NUEVO</span>
                                                    <div className="font-black text-emerald-900">{item.product_code || '-'}</div>
                                                </div>
                                                <div className="text-[11px] text-emerald-600">{item.name || item.product_name}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-300">-</td>
                                            <td className="px-4 py-3 text-center font-black text-emerald-700 underline decoration-2 decoration-emerald-400">{item.quantity}</td>
                                            <td className="px-4 py-3 text-right font-black text-emerald-700">{formatCurrency(item.price || item.unit_price)}</td>
                                        </tr>
                                    ))}

                                    {/* Eliminados */}
                                    {removed.map(item => (
                                        <tr key={item.id} className="bg-rose-50/40 grayscale italic">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-rose-500 text-white text-[9px] px-1.5 py-0.5 rounded uppercase leading-none">ELIMINADO</span>
                                                    <div className="font-black text-rose-900">{item.product_code || '-'}</div>
                                                </div>
                                                <div className="text-[11px] text-rose-600 line-through">{item.name || item.product_name}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center font-black text-rose-700">{item.quantity}</td>
                                            <td className="px-4 py-3 text-center text-rose-300">-</td>
                                            <td className="px-4 py-3 text-right font-black text-rose-700 line-through">{formatCurrency(item.price || item.unit_price)}</td>
                                        </tr>
                                    ))}

                                    {added.length === 0 && removed.length === 0 && modified.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-8 text-center text-gray-400 font-bold">
                                                No hay cambios en los productos o cantidades.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Sección de Notas */}
                    {notesChanged && (
                        <div className="space-y-4">
                            <h3 className="text-base font-black text-gray-800 flex items-center gap-2 uppercase tracking-wider">
                                <Icons.MessageSquare className="w-4 h-4" />
                                Notas y Observaciones
                            </h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Snapshot Original</span>
                                    <div className="text-sm text-gray-500 whitespace-pre-wrap font-medium bg-white p-3 rounded-lg border border-gray-100 min-h-[100px]">
                                        {snapNotes || <span className="italic text-gray-300">Sin notas registradas</span>}
                                    </div>
                                </div>
                                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest block mb-2">Versión Actual</span>
                                    <div className="text-sm text-gray-900 whitespace-pre-wrap font-bold bg-white p-3 rounded-lg border border-blue-100 min-h-[100px] underline decoration-blue-200 decoration-wavy">
                                        {currentNotes || <span className="italic text-gray-300">Sin notas registradas</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-medium">
                        * Los cambios resaltados indican discrepancias con el empaque original.
                    </p>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={async () => {
                                setIsValidating(true);
                                try {
                                    await onValidateNotes(order.id);
                                    onClose();
                                } finally {
                                    setIsValidating(false);
                                }
                            }}
                            disabled={isValidating}
                            className="px-6 py-2 text-sm font-black text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isValidating ? (
                                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Icons.CheckCircle className="w-4 h-4" />
                            )}
                            {(added.length > 0 || removed.length > 0 || modified.some(m => m.qtyChanged))
                                ? 'Aceptar Cambios y Habilitar Entrega'
                                : modified.some(m => !m.qtyChanged && m.priceChanged)
                                    ? 'Aceptar Cambio de Precio y Habilitar'
                                    : (notesChanged ? 'Validar Notas y Habilitar Entrega' : 'Habilitar Entrega (Sin Cambios Visuales)')}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 text-sm font-black text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 shadow-sm transition-all active:scale-95"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderChangesModal;
